/**
 * BloomPulse — an additive glow that breathes: its alpha and scale ease up
 * and back on a slow sine, forever. The "premium ambience" accent — drop one
 * behind a focal object (the Nexus vortex, a crystal, a portal) and it gains
 * a living halo. Composable: `moveTo` re-anchors it so it can ride a moving
 * target.
 *
 * Pixi stays behind the seam (createSprite + glow_soft + ISprite tweens).
 */
import { createSprite } from '../../rendering/utils/Sprite';
import type { ISprite, SpriteOptions, VfxBlendMode } from '../../rendering/IRenderer';
import { Z_LAYERS } from '../../rendering/Z_LAYERS';

export interface BloomPulseOptions {
  x: number;
  y: number;
  /** Glow diameter at base scale (px). */
  size?: number;
  color?: number;
  /** Alpha breathing band. */
  alphaMin?: number;
  alphaMax?: number;
  /** Scale breathing band (1 = base size). */
  scaleMin?: number;
  scaleMax?: number;
  /** Full breath cycle (ms) — slow reads as calm. */
  periodMs?: number;
  depth?: number;
  blendMode?: VfxBlendMode;
  /** Optional display parent (Scene FX v3 M1) — the sprite adds itself to this
   *  container so AtmosphereLayer dims the layer via container.alpha. */
  parent?: { addChild(child: unknown): void };
  spriteFactory?: (opts: SpriteOptions) => ISprite | null;
}

const DEFAULTS = {
  size: 220,
  color: 0xffffff,
  alphaMin: 0.12,
  alphaMax: 0.4,
  scaleMin: 0.9,
  scaleMax: 1.25,
  periodMs: 3200,
  blendMode: 'add' as VfxBlendMode,
};

export class BloomPulse {
  private sprite: ISprite | null = null;
  private destroyed = false;

  constructor(opts: BloomPulseOptions) {
    const factory = opts.spriteFactory ?? ((o: SpriteOptions) => createSprite(null, o));
    const size = opts.size ?? DEFAULTS.size;
    const color = opts.color ?? DEFAULTS.color;
    const alphaMin = opts.alphaMin ?? DEFAULTS.alphaMin;
    const alphaMax = opts.alphaMax ?? DEFAULTS.alphaMax;
    const scaleMin = opts.scaleMin ?? DEFAULTS.scaleMin;
    const scaleMax = opts.scaleMax ?? DEFAULTS.scaleMax;
    const periodMs = opts.periodMs ?? DEFAULTS.periodMs;

    const sprite = factory({
      x: opts.x,
      y: opts.y,
      initialTextureKey: 'glow_soft',
      width: size,
      height: size,
      depth: opts.depth ?? Z_LAYERS.ATMOSPHERE,
      alpha: alphaMin,
      parent: opts.parent,
    });
    if (!sprite) return;
    this.sprite = sprite;

    sprite.setTint(color);
    sprite.setBlendMode(opts.blendMode ?? DEFAULTS.blendMode);
    sprite.setScale(scaleMin);

    // Breathe alpha + scale on the same slow sine (yoyo, infinite).
    sprite.tween({ alpha: alphaMax }, { durationMs: periodMs / 2, ease: 'sineInOut', yoyo: true, repeat: -1 });
    sprite.tween(
      { scaleX: scaleMax, scaleY: scaleMax },
      { durationMs: periodMs / 2, ease: 'sineInOut', yoyo: true, repeat: -1 },
    );
  }

  /** Re-anchor the bloom — lets it ride a moving focal point. */
  moveTo(x: number, y: number): void {
    this.sprite?.setPosition(x, y);
  }

  isAlive(): boolean {
    return !this.destroyed && this.sprite !== null;
  }

  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    if (this.sprite) {
      try { this.sprite.killAllTweens(); } catch { /* gone */ }
      try { this.sprite.destroy(); } catch { /* gone */ }
      this.sprite = null;
    }
  }
}
